import { useId, useState } from 'react';
import { DateTime } from 'luxon';

import { Time } from '../StrokeIcons';
import type { ButtonProps } from './Button';
import Button from './Button';
import CalendarPopover from './CalendarPopover';
import { cleanId } from './types';

export type DateButtonProps = Omit<ButtonProps, 'children' | 'onClick' | 'type' | 'icon'> & {
  /** Timestamp in milliseconds */
  value?: number;
  /** Called when a date is selected, with date in "yyyy-MM-dd" format */
  onDateChange?: (date: string) => void;
  /** Hide year when it matches the current year (e.g., "Apr 5" vs "Apr 5, 2024") */
  hideCurrentYear?: boolean;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Icon to display. Pass `null` to hide the icon. Defaults to calendar icon. */
  icon?: React.ReactNode | null;
};

export default function DateButton({
  value,
  onDateChange,
  hideCurrentYear = false,
  placeholder = 'Select date',
  size = 'md',
  color,
  tone,
  icon,
  ...buttonProps
}: DateButtonProps) {
  const id = cleanId(useId());
  const [isOpen, setIsOpen] = useState(false);
  const popoverId = `date-popover-${id}`;
  const anchorName = `--date-anchor-${id}`;

  // Format the date for display
  let displayText = placeholder;
  if (value && !isNaN(value)) {
    const valueDate = DateTime.fromMillis(value);
    const now = DateTime.now();
    const isSameYear = valueDate.year === now.year;

    if (hideCurrentYear && isSameYear) {
      // Show date without year (e.g., "Jan 15")
      displayText = valueDate.toFormat('MMM d');
    } else {
      // Show full date with year (e.g., "Jan 15, 2024")
      displayText = valueDate.toLocaleString(DateTime.DATE_MED);
    }
  }

  return (
    <>
      <Button
        size={size}
        color={color}
        tone={tone}
        inputStyle
        icon={icon === null ? undefined : (icon ?? <Time.Calendar />)}
        active={isOpen}
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        {...buttonProps}>
        {displayText}
      </Button>
      <CalendarPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        color={color}
        tone={tone}
        value={value}
        onDateChange={onDateChange}
        onOpenChange={setIsOpen}
      />
    </>
  );
}
